import { NearbyResult, NearbyType } from '@habity/types';

/**
 * Deduplicates nearby items by place_id and name.
 * When duplicates are found, keeps the item with the shortest distance.
 * 
 * @param items - Array of nearby results to deduplicate
 * @returns Array of unique nearby results
 */
export const deduplicateNearbyItems = (items: NearbyResult[]): NearbyResult[] => {
  const uniqueItemsMap = new Map<string, NearbyResult>();
  
  items.forEach(item => {
    // Use place_id as primary key, fallback to name if place_id is missing
    const key = item.place_id || item.name.toLowerCase().trim();
    const existing = uniqueItemsMap.get(key);
    
    // Keep the item with the shorter distance
    if (!existing || item.distance < existing.distance) {
      uniqueItemsMap.set(key, item);
    }
  });
  
  return Array.from(uniqueItemsMap.values());
};

/**
 * Validates a nearby item to ensure it has all required fields
 */
export const validateNearbyItem = (item: Partial<NearbyResult>): boolean => {
  return !!(
    item.name &&
    item.type &&
    typeof item.distance === 'number' &&
    item.distance >= 0
  );
};

/**
 * Sorts nearby items by distance (ascending)
 */
export const sortNearbyByDistance = (items: NearbyResult[]): NearbyResult[] => {
  return [...items].sort((a, b) => a.distance - b.distance);
};

/**
 * Groups nearby items by type
 */
export const groupNearbyByType = (
  items: NearbyResult[]
): Record<NearbyType, NearbyResult[]> => {
  return items.reduce((acc, item) => {
    if (!acc[item.type]) {
      acc[item.type] = [];
    }
    acc[item.type].push(item);
    return acc;
  }, {} as Record<NearbyType, NearbyResult[]>);
};

/**
 * Filters nearby items by maximum distance
 */
export const filterNearbyByDistance = (
  items: NearbyResult[],
  maxDistance: number
): NearbyResult[] => {
  return items.filter((item) => item.distance <= maxDistance);
};

/**
 * Gets the closest item of a specific type
 */
export const getClosestByType = (
  items: NearbyResult[],
  type: NearbyType
): NearbyResult | null => {
  const filtered = items.filter((item) => item.type === type);
  if (filtered.length === 0) return null;
  
  return filtered.reduce((closest, current) =>
    current.distance < closest.distance ? current : closest
  );
};

/**
 * Formats distance for display
 */
export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * Creates a Google Maps link for a nearby location
 */
export const createGoogleMapsLink = (
  name: string,
  address?: string,
  city?: string,
  zip?: string
): string => {
  const parts = [name];
  if (address) parts.push(address);
  if (zip) parts.push(zip);
  if (city) parts.push(city);
  
  const query = parts.join(', ');
  return `https://maps.google.com/maps?q=${encodeURIComponent(query)}`;
};
